import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { useTranslation } from "react-i18next";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://praxisbharwani-backend.vercel.app/api";

const REFERRAL_SELECTION_STORAGE_KEY = "praxisReferralSelection";

const DEFAULT_FEATURE_TOGGLES = {
  prescriptionEnabled: true,
  appointmentEnabled: true,
  referralEnabled: true,
  phoneEnabled: true,
};

const INITIAL_PERSONAL_DETAILS = {
  salutation: "",
  surname: "",
  name: "",
  dateOfBirth: "",
  phone: "",
  email: "",
  insuranceMethod: "publicly-insured",
  consentRequired: false,
  consentMarketing: false,
};

const Contactpage = ({ section = "full", openTerminModal }) => {
  const showOfficeInfo = section !== "prescriptionOnly";
  const showPrescription = section !== "contactOnly";
  const showMap = section !== "prescriptionOnly";
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentReferralStep, setCurrentReferralStep] = useState(1);
  const [medications, setMedications] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [selectedReferralSpecialistId, setSelectedReferralSpecialistId] = useState("");
  const [isMedicationDropdownOpen, setIsMedicationDropdownOpen] =
    useState(false);
  const [isReferralDropdownOpen, setIsReferralDropdownOpen] = useState(false);
  const [isSalutationDropdownOpen, setIsSalutationDropdownOpen] =
    useState(false);
  const [prescriptionComment, setPrescriptionComment] = useState("");
  const [referralReason, setReferralReason] = useState("");
  const [rememberReferralSelection, setRememberReferralSelection] = useState(false);
  const [medicalCardStatus, setMedicalCardStatus] = useState("");
  const [stepOneError, setStepOneError] = useState("");
  const [stepTwoErrors, setStepTwoErrors] = useState({});
  const [stepThreeError, setStepThreeError] = useState("");
  const [referralStepOneError, setReferralStepOneError] = useState("");
  const [referralStepTwoErrors, setReferralStepTwoErrors] = useState({});
  const [referralStepThreeError, setReferralStepThreeError] = useState("");
  const [sendOrderLoading, setSendOrderLoading] = useState(false);
  const [sendOrderError, setSendOrderError] = useState("");
  const [sendOrderSuccess, setSendOrderSuccess] = useState("");
  const [sendReferralLoading, setSendReferralLoading] = useState(false);
  const [sendReferralError, setSendReferralError] = useState("");
  const [sendReferralSuccess, setSendReferralSuccess] = useState("");
  const [specialistsLoading, setSpecialistsLoading] = useState(false);
  const [specialistsError, setSpecialistsError] = useState("");
  const [featureToggles, setFeatureToggles] = useState(DEFAULT_FEATURE_TOGGLES);
  const medicationDropdownRef = useRef(null);
  const salutationDropdownRef = useRef(null);
  const referralDropdownRef = useRef(null);
  const closeAfterSuccessTimeoutRef = useRef(null);
  const [personalDetails, setPersonalDetails] = useState(INITIAL_PERSONAL_DETAILS);

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";
  const isGerman = currentLanguage.toLowerCase().startsWith("de");
  const resolveSpecialistLabel = useCallback((specialist) => {
    if (!specialist) {
      return "";
    }

    const primary = isGerman ? specialist.nameDe : specialist.nameEn;
    return primary || specialist.nameEn || specialist.nameDe || "";
  }, [isGerman]);

  const PRESCRIPTION_STEPS = useMemo(
    () => [
      t("contact_modal_step_order_prescription"),
      t("contact_modal_step_your_details"),
      t("contact_modal_step_confirm_medical_card"),
      t("contact_modal_step_review"),
    ],
    [t],
  );

  const REFERRAL_STEPS = useMemo(
    () => [
      t("contact_modal_step_get_referral"),
      t("contact_modal_step_your_details"),
      t("contact_modal_step_confirm_medical_card"),
      t("contact_modal_step_review"),
    ],
    [t],
  );

  const SALUTATION_OPTIONS = useMemo(
    () => [
      { value: "mr", label: t("contact_modal_salutation_mr") },
      { value: "mrs", label: t("contact_modal_salutation_mrs") },
      { value: "ms", label: t("contact_modal_salutation_ms") },
    ],
    [t],
  );

  const redirectToMaps = () => {
    const link =
      "https://www.google.com/maps/search/?api=1&query=Kalkumer+Str.+151%2C+40468+D%C3%BCsseldorf";
    window.open(link, "_blank");
  };

  const openMail = () => {
    const link = "mailto:praxisbharwani@gmail.com";
    window.open(link, "_blank");
  };

  const openPhoneNumber = () => {
    const link = "tel:+492114792626";
    window.open(link, "_blank");
  };

  const resetReferralFormState = () => {
    setCurrentReferralStep(1);
    setSelectedReferralSpecialistId("");
    setReferralReason("");
    setRememberReferralSelection(false);
    setIsReferralDropdownOpen(false);
    setPersonalDetails(INITIAL_PERSONAL_DETAILS);
    setMedicalCardStatus("");
    setReferralStepOneError("");
    setReferralStepTwoErrors({});
    setReferralStepThreeError("");
    setSendReferralLoading(false);
    setSendReferralError("");
    setSendReferralSuccess("");
    setSpecialistsError("");
  };

  const resetPrescriptionFormState = () => {
    setCurrentStep(1);
    setSelectedMedications([]);
    setPrescriptionComment("");
    setMedicalCardStatus("");
    setPersonalDetails(INITIAL_PERSONAL_DETAILS);
    setIsMedicationDropdownOpen(false);
    setIsSalutationDropdownOpen(false);
    setStepOneError("");
    setStepTwoErrors({});
    setStepThreeError("");
    setSendOrderLoading(false);
    setSendOrderError("");
    setSendOrderSuccess("");
  };

  const clearCloseAfterSuccessTimer = () => {
    if (closeAfterSuccessTimeoutRef.current) {
      clearTimeout(closeAfterSuccessTimeoutRef.current);
      closeAfterSuccessTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearCloseAfterSuccessTimer();
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadFeatureToggles = async () => {
      try {
        const response = await fetch(`${API_BASE}/feature-toggles`);
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error("Unable to load feature toggles");
        }

        if (!ignore) {
          setFeatureToggles({
            ...DEFAULT_FEATURE_TOGGLES,
            ...data,
          });
        }
      } catch (_error) {
        if (!ignore) {
          setFeatureToggles(DEFAULT_FEATURE_TOGGLES);
        }
      }
    };

    loadFeatureToggles();

    return () => {
      ignore = true;
    };
  }, []);

  const openPrescriptionModal = () => {
    if (!featureToggles.prescriptionEnabled) {
      return;
    }

    clearCloseAfterSuccessTimer();
    resetPrescriptionFormState();
    setIsPrescriptionModalOpen(true);
  };

  const openReferralModal = () => {
    if (!featureToggles.referralEnabled) {
      return;
    }

    clearCloseAfterSuccessTimer();
    resetReferralFormState();

    try {
      const rawValue = localStorage.getItem(REFERRAL_SELECTION_STORAGE_KEY);

      if (rawValue) {
        const parsedValue = JSON.parse(rawValue);

        if (parsedValue?.expiresAt > Date.now()) {
          setSelectedReferralSpecialistId(
            parsedValue.selectedReferralSpecialistId || "",
          );
          setReferralReason(parsedValue.referralReason || "");
          setRememberReferralSelection(true);
        } else {
          localStorage.removeItem(REFERRAL_SELECTION_STORAGE_KEY);
        }
      }
    } catch (_error) {
      localStorage.removeItem(REFERRAL_SELECTION_STORAGE_KEY);
    }

    setIsReferralModalOpen(true);
  };

  const closePrescriptionModal = () => {
    clearCloseAfterSuccessTimer();
    resetPrescriptionFormState();
    setIsPrescriptionModalOpen(false);
  };

  const closeReferralModal = () => {
    clearCloseAfterSuccessTimer();
    resetReferralFormState();
    setIsReferralModalOpen(false);
  };

  const handleAppointmentAction = () => {
    if (!featureToggles.appointmentEnabled) {
      return;
    }

    if (openTerminModal) {
      openTerminModal();
      return;
    }

    openPhoneNumber();
  };

  const handlePhoneAction = () => {
    if (!featureToggles.phoneEnabled) {
      return;
    }

    openPhoneNumber();
  };

  useEffect(() => {
    if (!isPrescriptionModalOpen || medications.length) {
      return;
    }

    let ignore = false;

    const loadMedications = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/medications?activeOnly=true&limit=1000`,
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.message || t("contact_modal_error_load_medications"));
        }

        if (!ignore) {
          setMedications(Array.isArray(data.data) ? data.data : []);
        }
      } catch (error) {
        // Silently fail for now
      }
    };

    loadMedications();

    return () => {
      ignore = true;
    };
  }, [isPrescriptionModalOpen, medications.length, t]);

  useEffect(() => {
    if (!isReferralModalOpen || specialists.length) {
      return;
    }

    let ignore = false;

    const loadSpecialists = async () => {
      setSpecialistsLoading(true);
      setSpecialistsError("");

      try {
        const response = await fetch(`${API_BASE}/specialists/public`);
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.message || t("contact_modal_error_load_specialists"));
        }

        if (!ignore) {
          const entries = Array.isArray(data.data) ? data.data : [];
          entries.sort((a, b) =>
            resolveSpecialistLabel(a).localeCompare(
              resolveSpecialistLabel(b),
              currentLanguage,
              { sensitivity: "base" },
            ),
          );
          setSpecialists(entries);
        }
      } catch (error) {
        if (!ignore) {
          setSpecialistsError(
            error.message || t("contact_modal_error_load_specialists"),
          );
        }
      } finally {
        if (!ignore) {
          setSpecialistsLoading(false);
        }
      }
    };

    loadSpecialists();

    return () => {
      ignore = true;
    };
  }, [currentLanguage, isReferralModalOpen, resolveSpecialistLabel, specialists.length, t]);

  const selectedSalutationLabel = useMemo(() => {
    const selectedOption = SALUTATION_OPTIONS.find(
      (option) => option.value === personalDetails.salutation,
    );
    return selectedOption
      ? selectedOption.label
      : t("contact_modal_select_salutation");
  }, [personalDetails.salutation, SALUTATION_OPTIONS, t]);

  const insuranceMethodLabel = useMemo(
    () =>
      personalDetails.insuranceMethod === "privately-insured"
        ? t("contact_modal_insurance_private")
        : t("contact_modal_insurance_public"),
    [personalDetails.insuranceMethod, t],
  );

  const medicalCardStatusLabel = useMemo(() => {
    if (medicalCardStatus === "collect-pharmacy") {
      return t("contact_modal_medical_card_collect");
    }

    if (medicalCardStatus === "come-clinic") {
      return t("contact_modal_medical_card_come_clinic");
    }

    return t("contact_modal_not_selected");
  }, [medicalCardStatus, t]);

  const referralMedicalCardStatusLabel = useMemo(() => {
    if (medicalCardStatus === "collect-pharmacy") {
      return t("contact_modal_referral_medical_card_scanned");
    }

    if (medicalCardStatus === "come-clinic") {
      return t("contact_modal_medical_card_come_clinic");
    }

    return t("contact_modal_not_selected");
  }, [medicalCardStatus, t]);

  const reviewNameLine = useMemo(() => {
    const nameParts = [
      personalDetails.surname,
      personalDetails.name,
      personalDetails.dateOfBirth,
    ].filter(Boolean);

    if (!nameParts.length) {
      return t("contact_modal_not_provided");
    }

    return `${selectedSalutationLabel !== t("contact_modal_select_salutation") ? `${selectedSalutationLabel} ` : ""}${nameParts.join(" - ")}`;
  }, [
    personalDetails.dateOfBirth,
    personalDetails.name,
    personalDetails.surname,
    selectedSalutationLabel,
    t,
  ]);

  const selectedReferralSpecialistLabel = useMemo(() => {
    if (specialistsLoading) {
      return t("contact_modal_loading_specialists");
    }

    return (
      resolveSpecialistLabel(
        specialists.find(
          (specialist) => specialist.id === selectedReferralSpecialistId,
        ),
      ) || t("contact_modal_select_specialist")
    );
  }, [resolveSpecialistLabel, selectedReferralSpecialistId, specialists, specialistsLoading, t]);

  useEffect(() => {
    if (!isMedicationDropdownOpen) {
      return;
    }

    const handleOutsideClick = (event) => {
      if (
        medicationDropdownRef.current &&
        !medicationDropdownRef.current.contains(event.target)
      ) {
        setIsMedicationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isMedicationDropdownOpen]);

  useEffect(() => {
    if (!isSalutationDropdownOpen) {
      return;
    }

    const handleOutsideClick = (event) => {
      if (
        salutationDropdownRef.current &&
        !salutationDropdownRef.current.contains(event.target)
      ) {
        setIsSalutationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isSalutationDropdownOpen]);

  useEffect(() => {
    if (!isReferralDropdownOpen) {
      return;
    }

    const handleOutsideClick = (event) => {
      if (
        referralDropdownRef.current &&
        !referralDropdownRef.current.contains(event.target)
      ) {
        setIsReferralDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isReferralDropdownOpen]);



  const handleConfirmMedication = () => {
    setIsMedicationDropdownOpen(false);
    setStepOneError("");
    setCurrentStep(2);
  };

  const handlePersonalDetailsChange = (field, value) => {
    setPersonalDetails((current) => ({
      ...current,
      [field]: value,
    }));

    setStepTwoErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleConfirmPersonalDetails = () => {
    const nextErrors = {};

    if (!personalDetails.salutation) {
      nextErrors.salutation = t("contact_modal_error_select_salutation");
    }

    if (!personalDetails.surname.trim()) {
      nextErrors.surname = t("contact_modal_error_surname_required");
    }

    if (!personalDetails.name.trim()) {
      nextErrors.name = t("contact_modal_error_name_required");
    }

    if (!personalDetails.dateOfBirth.trim()) {
      nextErrors.dateOfBirth = t("contact_modal_error_dob_required");
    }

    if (!personalDetails.email.trim()) {
      nextErrors.email = t("contact_modal_error_email_required");
    } else if (!/^\S+@\S+\.\S+$/.test(personalDetails.email.trim())) {
      nextErrors.email = t("contact_modal_error_email_invalid");
    }

    if (!personalDetails.consentRequired) {
      nextErrors.consentRequired = t("contact_modal_error_consent_required");
    }

    if (Object.keys(nextErrors).length) {
      setStepTwoErrors(nextErrors);
      return;
    }

    setStepTwoErrors({});
    setIsSalutationDropdownOpen(false);
    setCurrentStep(3);
  };

  const validatePersonalDetails = () => {
    const nextErrors = {};

    if (!personalDetails.salutation) {
      nextErrors.salutation = t("contact_modal_error_select_salutation");
    }

    if (!personalDetails.surname.trim()) {
      nextErrors.surname = t("contact_modal_error_surname_required");
    }

    if (!personalDetails.name.trim()) {
      nextErrors.name = t("contact_modal_error_name_required");
    }

    if (!personalDetails.dateOfBirth.trim()) {
      nextErrors.dateOfBirth = t("contact_modal_error_dob_required");
    }

    if (!personalDetails.email.trim()) {
      nextErrors.email = t("contact_modal_error_email_required");
    } else if (!/^\S+@\S+\.\S+$/.test(personalDetails.email.trim())) {
      nextErrors.email = t("contact_modal_error_email_invalid");
    }

    if (!personalDetails.consentRequired) {
      nextErrors.consentRequired = t("contact_modal_error_consent_required");
    }

    return nextErrors;
  };

  const handleContinueMedicalCard = () => {
    if (!medicalCardStatus) {
      setStepThreeError(t("contact_modal_error_select_option"));
      return;
    }

    setStepThreeError("");
    setSendOrderError("");
    setSendOrderSuccess("");
    setCurrentStep(4);
  };

  const handleConfirmReferralRequest = () => {
    if (!selectedReferralSpecialistId || !referralReason.trim()) {
      setReferralStepOneError(
        t("contact_modal_error_select_specialist_reason"),
      );
      return;
    }

    if (rememberReferralSelection) {
      const expiresAt = Date.now() + 120 * 24 * 60 * 60 * 1000;
      localStorage.setItem(
        REFERRAL_SELECTION_STORAGE_KEY,
        JSON.stringify({
          selectedReferralSpecialistId,
          referralReason: referralReason.trim(),
          expiresAt,
        }),
      );
    } else {
      localStorage.removeItem(REFERRAL_SELECTION_STORAGE_KEY);
    }

    setReferralStepOneError("");
    setIsReferralDropdownOpen(false);
    setCurrentReferralStep(2);
  };

  const handleConfirmReferralPersonalDetails = () => {
    const nextErrors = validatePersonalDetails();

    if (Object.keys(nextErrors).length) {
      setReferralStepTwoErrors(nextErrors);
      return;
    }

    setReferralStepTwoErrors({});
    setIsSalutationDropdownOpen(false);
    setCurrentReferralStep(3);
  };

  const handleContinueReferralMedicalCard = () => {
    if (!medicalCardStatus) {
      setReferralStepThreeError(t("contact_modal_error_select_option"));
      return;
    }

    setReferralStepThreeError("");
    setSendReferralError("");
    setSendReferralSuccess("");
    setCurrentReferralStep(4);
  };

  const parseDobToISO = (dobText) => {
    if (!dobText) {
      return null;
    }

    const trimmed = dobText.trim();
    const ddMmYyyy = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const match = trimmed.match(ddMmYyyy);

    if (match) {
      const [, day, month, year] = match;
      return `${year}-${month}-${day}T00:00:00.000Z`;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed.toISOString();
  };

  const handleSendOrder = async () => {
    if (sendOrderLoading) {
      return;
    }

    const dateOfBirthISO = parseDobToISO(personalDetails.dateOfBirth);
    if (!dateOfBirthISO) {
      setCurrentStep(2);
      setStepTwoErrors((current) => ({
        ...current,
        dateOfBirth: t("contact_modal_error_dob_invalid"),
      }));
      return;
    }

    const salutationMap = { mr: "MR", mrs: "MRS", ms: "MS" };
    const insuranceMap = {
      "publicly-insured": "PUBLIC",
      "privately-insured": "PRIVATE",
    };

    const medicalCardNote =
      medicalCardStatus === "collect-pharmacy"
        ? "Medical card status: Can collect directly from pharmacy"
        : "Medical card status: Must come to clinic first";

    const composedNotes = [prescriptionComment?.trim(), medicalCardNote]
      .filter(Boolean)
      .join("\n");

    const payload = {
      salutation: salutationMap[personalDetails.salutation],
      surname: personalDetails.surname.trim(),
      firstName: personalDetails.name.trim(),
      dateOfBirth: dateOfBirthISO,
      phone: personalDetails.phone?.trim() || null,
      email: personalDetails.email.trim(),
      insuranceMethod: insuranceMap[personalDetails.insuranceMethod],
      consentDataProcessing: !!personalDetails.consentRequired,
      consentMarketing: !!personalDetails.consentMarketing,
      notes: composedNotes || null,
      prescriptionItems: selectedMedications.map((item) => ({
        medicationId: item.id,
        quantity: 1,
      })),
    };

    try {
      setSendOrderLoading(true);
      setSendOrderError("");
      setSendOrderSuccess("");

      const response = await fetch(`${API_BASE}/prescriptions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || t("contact_modal_error_send_order"));
      }

      setSendOrderSuccess(t("contact_modal_success_order"));
      setSendOrderError("");

      clearCloseAfterSuccessTimer();
      closeAfterSuccessTimeoutRef.current = setTimeout(() => {
        closePrescriptionModal();
      }, 1200);
    } catch (error) {
      setSendOrderError(error.message || t("contact_modal_error_send_order"));
      setSendOrderSuccess("");
    } finally {
      setSendOrderLoading(false);
    }
  };

  const handleSendReferral = async () => {
    if (sendReferralLoading) {
      return;
    }

    const dateOfBirthISO = parseDobToISO(personalDetails.dateOfBirth);
    if (!dateOfBirthISO) {
      setCurrentReferralStep(2);
      setReferralStepTwoErrors((current) => ({
        ...current,
        dateOfBirth: t("contact_modal_error_dob_invalid"),
      }));
      return;
    }

    const selectedSpecialist = specialists.find(
      (specialist) => specialist.id === selectedReferralSpecialistId,
    );

    if (!selectedSpecialist) {
      setCurrentReferralStep(1);
      setReferralStepOneError(t("contact_modal_error_select_specialist_send"));
      return;
    }

    const salutationMap = { mr: "MR", mrs: "MRS", ms: "MS" };
    const insuranceMap = {
      "publicly-insured": "PUBLIC",
      "privately-insured": "PRIVATE",
    };

    const medicalCardNote =
      medicalCardStatus === "collect-pharmacy"
        ? "Medical card status: Can collect directly from pharmacy"
        : "Medical card status: Must come to clinic first";

    const payload = {
      specialistType: resolveSpecialistLabel(selectedSpecialist),
      reason: [referralReason.trim(), medicalCardNote].filter(Boolean).join("\n\n"),
      urgency: null,
      salutation: salutationMap[personalDetails.salutation],
      surname: personalDetails.surname.trim(),
      firstName: personalDetails.name.trim(),
      dateOfBirth: dateOfBirthISO,
      phone: personalDetails.phone?.trim() || null,
      email: personalDetails.email.trim(),
      insuranceMethod: insuranceMap[personalDetails.insuranceMethod],
      consentDataProcessing: !!personalDetails.consentRequired,
      consentMarketing: !!personalDetails.consentMarketing,
    };

    try {
      setSendReferralLoading(true);
      setSendReferralError("");
      setSendReferralSuccess("");

      const response = await fetch(`${API_BASE}/referrals`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || t("contact_modal_error_send_referral"));
      }

      setSendReferralSuccess(t("contact_modal_success_referral"));
      setSendReferralError("");

      clearCloseAfterSuccessTimer();
      closeAfterSuccessTimeoutRef.current = setTimeout(() => {
        closeReferralModal();
      }, 1200);
    } catch (error) {
      setSendReferralError(
        error.message || t("contact_modal_error_send_referral"),
      );
      setSendReferralSuccess("");
    } finally {
      setSendReferralLoading(false);
    }
  };

  const prescriptionQuickActions = [
    featureToggles.prescriptionEnabled && {
      key: "prescription",
      title: t("contact_modal_quick_order_prescription"),
      iconClass: "ti-receipt",
      onActivate: openPrescriptionModal,
    },
    featureToggles.appointmentEnabled && {
      key: "appointment",
      title: t("contact_modal_quick_make_appointment"),
      iconClass: "ti-calendar",
      onActivate: handleAppointmentAction,
    },
    featureToggles.referralEnabled && {
      key: "referral",
      title: t("contact_modal_quick_get_referral"),
      iconClass: "ti-agenda",
      onActivate: openReferralModal,
    },
    featureToggles.phoneEnabled && {
      key: "phone",
      title: t("contact_modal_quick_book_phone"),
      iconClass: "ti-mobile",
      onActivate: handlePhoneAction,
    },
  ].filter(Boolean);

  return (
    <section
      id={showPrescription ? "bestellung" : undefined}
      className="wpo-contact-pg-section section-padding"
    >
      <div className="container">
        <div className="row">
          <div className="col col-lg-10 offset-lg-1">
            {showOfficeInfo && (
              <div className="office-info">
                <div className="row">
                  <div className="col col-xl-3 col-lg-6 col-md-6 col-12">
                    <div className="office-info-item">
                      <div className="office-info-icon">
                        <div
                          className="icon"
                          onClick={redirectToMaps}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="fi flaticon-placeholder"></i>
                        </div>
                      </div>
                      <div className="office-info-text">
                        <h2 style={{ fontWeight: "bold" }}>
                          {t("contact_address")}
                        </h2>
                        <p>Kalkumer Str. 151, 40468 Düsseldorf</p>
                      </div>
                    </div>
                  </div>
                  <div className="col col-xl-3 col-lg-6 col-md-6 col-12">
                    <div className="office-info-item">
                      <div className="office-info-icon">
                        <div
                          className="icon"
                          onClick={openMail}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="fi flaticon-email"></i>
                        </div>
                      </div>
                      <div className="office-info-text">
                        <h2 style={{ fontWeight: "bold" }}>
                          {t("contact_email")}
                        </h2>
                        <p>praxisbharwani@gmail.com</p>
                      </div>
                    </div>
                  </div>
                  <div className="col col-xl-3 col-lg-6 col-md-6 col-12">
                    <div className="office-info-item">
                      <div className="office-info-icon">
                        <div
                          className="icon"
                          onClick={openPhoneNumber}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="fi flaticon-phone-call"></i>
                        </div>
                      </div>
                      <div className="office-info-text">
                        <h2 style={{ fontWeight: "bold" }}>
                          {t("contact_phone")}
                        </h2>
                        <p>0211 4792626</p>
                      </div>
                    </div>
                  </div>
                  <div className="col col-xl-3 col-lg-6 col-md-6 col-12">
                    <div className="office-info-item">
                      <div className="office-info-icon">
                        <div className="icon">
                          <i className="fa fa-fax"></i>
                        </div>
                      </div>
                      <div className="office-info-text">
                        <h2 style={{ fontWeight: "bold" }}>
                          {t("contact_fax")}
                        </h2>
                        <p>0211/4963909</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showPrescription && prescriptionQuickActions.length > 0 && (
              <div className="wpo-contact-title prescription-hub-title">
                <h2 style={{ fontWeight: "bold" }}>{t("hero_feat_3")}</h2>
                {/*<p>{t('contact_text_1')}<br /> <b>{t('contact_text_2')}</b></p>*/}
                <div className="prescription-cta-section wpo-department-section-s2">
                  <div className="prescription-card-grid">
                    {prescriptionQuickActions.map((action) => (
                      <div
                        key={action.key}
                        className={`prescription-action-card${action.wide ? " is-wide" : ""}`}
                        onClick={action.onActivate}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            action.onActivate();
                          }
                        }}
                      >
                        <div className="prescription-action-icon">
                          <i className={action.iconClass}></i>
                        </div>
                        <span>{action.title}</span>
                        <i className="ti-arrow-right prescription-action-arrow" aria-hidden="true"></i>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {/*<div className="wpo-contact-form-area">*/}
            {/*    /!*<ContactForm/>*!/*/}
            {/*    Coming Soon...*/}
            {/*</div>*/}
          </div>
        </div>
      </div>
      {showMap && (
        <section className="wpo-contact-map-section">
          <div className="wpo-contact-map">
            <iframe
              title="map"
              src="https://maps.google.com/maps?q=Kalkumer+Str.+151,+40468+D%C3%BCsseldorf&output=embed"
            ></iframe>
          </div>
        </section>
      )}

      {showPrescription && (
        <Modal
          open={isPrescriptionModalOpen}
          onClose={closePrescriptionModal}
          center
          focusTrapped={false}
          classNames={{
            modal: "prescription-modal",
            overlay: "prescription-modal-overlay",
          }}
        >
        <div className="prescription-modal-scroll">
          <div className="prescription-modal-content">
            <div
              className="prescription-stepper"
              aria-label={t("contact_modal_aria_prescription_steps")}
            >
              {PRESCRIPTION_STEPS.map((stepTitle, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < currentStep;
                const isActive = stepNumber === currentStep;
                const itemClassName = `prescription-step-item${isCompleted ? " is-completed" : ""}${isActive ? " is-active" : ""}`;

                return (
                  <React.Fragment key={stepTitle}>
                    <div className={itemClassName}>
                      <span
                        className="prescription-step-circle"
                        aria-hidden="true"
                      >
                        {isCompleted ? (
                          <i className="ti-check"></i>
                        ) : (
                          stepNumber
                        )}
                      </span>
                      <span className="prescription-step-title">
                        {stepTitle}
                      </span>
                    </div>
                    {stepNumber !== PRESCRIPTION_STEPS.length && (
                      <span
                        className="prescription-step-separator"
                        aria-hidden="true"
                      >
                        <i className="ti-angle-right"></i>
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <p className="prescription-stepper-mobile-label">
              {t("contact_modal_step_label", { current: currentStep, total: PRESCRIPTION_STEPS.length })} &ndash; {PRESCRIPTION_STEPS[currentStep - 1]}
            </p>

            {currentStep === 1 && (
              <>
                <div className="prescription-modal-header">
                  <div className="prescription-modal-icon">
                    <i className="fi flaticon-diary"></i>
                  </div>
                  <p className="prescription-modal-kicker">
                    {t("contact_modal_kicker_prescription")}
                  </p>
                </div>

                {stepOneError && (
                  <p className="prescription-step-error">{stepOneError}</p>
                )}

                <div className="prescription-comment-field">
                  <label htmlFor="prescription-comment">
                    {t("contact_modal_comment_label_prescription")}
                  </label>
                  <textarea
                    id="prescription-comment"
                    rows="5"
                    placeholder={t("contact_modal_comment_placeholder_prescription")}
                    value={prescriptionComment}
                    onChange={(event) =>
                      setPrescriptionComment(event.target.value)
                    }
                  />
                </div>

                <div className="prescription-modal-actions">
                  <button
                    type="button"
                    className="theme-btn"
                    onClick={handleConfirmMedication}
                  >
                    {t("contact_modal_confirm_medication")}
                  </button>
                </div>
              </>
            )}

            {currentStep === 2 && (
              <div className="prescription-step-two">
                <button
                  type="button"
                  className="prescription-step-back"
                  onClick={() => {
                    setStepTwoErrors({});
                    setCurrentStep(1);
                  }}
                >
                  <i className="ti-angle-left"></i>
                  <span>{t("contact_modal_back")}</span>
                </button>

                <div className="prescription-modal-header prescription-step-header prescription-step-title-row">
                  <div className="prescription-modal-icon">
                    <i className="fi flaticon-user"></i>
                  </div>
                  <h2>{t("contact_modal_title_personal_details")}</h2>
                </div>

                <p>{t("contact_modal_fields_required")}</p>

                <div className="prescription-form-grid">
                  <label htmlFor="prescription-salutation">{t("contact_modal_label_salutation_required")}</label>
                  <div
                    className="prescription-custom-select"
                    ref={salutationDropdownRef}
                  >
                    <button
                      id="prescription-salutation"
                      type="button"
                      className={`prescription-custom-select-toggle${stepTwoErrors.salutation ? " prescription-invalid-field" : ""}`}
                      onClick={() =>
                        setIsSalutationDropdownOpen((current) => !current)
                      }
                      aria-haspopup="listbox"
                      aria-expanded={isSalutationDropdownOpen}
                    >
                      <span>{selectedSalutationLabel}</span>
                      <i
                        className={`ti-angle-${isSalutationDropdownOpen ? "up" : "down"}`}
                      ></i>
                    </button>

                    {isSalutationDropdownOpen && (
                      <div
                        className="prescription-custom-select-menu"
                        role="listbox"
                        aria-label={t("contact_modal_aria_salutation_options")}
                      >
                        {SALUTATION_OPTIONS.map((option) => (
                          <button
                            type="button"
                            className={`prescription-custom-select-option${personalDetails.salutation === option.value ? " is-selected" : ""}`}
                            key={option.value}
                            onClick={() => {
                              handlePersonalDetailsChange(
                                "salutation",
                                option.value,
                              );
                              setIsSalutationDropdownOpen(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <label htmlFor="prescription-surname">{t("contact_modal_label_surname_required")}</label>
                  <input
                    id="prescription-surname"
                    type="text"
                    className={stepTwoErrors.surname ? "prescription-invalid-field" : ""}
                    value={personalDetails.surname}
                    onChange={(event) =>
                      handlePersonalDetailsChange("surname", event.target.value)
                    }
                  />
                  <p className="prescription-field-hint">
                    {t("contact_modal_hint_insurance_name")}
                  </p>

                  <label htmlFor="prescription-name">{t("contact_modal_label_name_required")}</label>
                  <input
                    id="prescription-name"
                    type="text"
                    className={stepTwoErrors.name ? "prescription-invalid-field" : ""}
                    value={personalDetails.name}
                    onChange={(event) =>
                      handlePersonalDetailsChange("name", event.target.value)
                    }
                  />
                  <p className="prescription-field-hint">
                    {t("contact_modal_hint_insurance_name")}
                  </p>

                  <label htmlFor="prescription-dob">{t("contact_modal_label_dob_required")}</label>
                  <input
                    id="prescription-dob"
                    type="text"
                    className={stepTwoErrors.dateOfBirth ? "prescription-invalid-field" : ""}
                    placeholder={t("contact_modal_dob_placeholder")}
                    value={personalDetails.dateOfBirth}
                    onChange={(event) =>
                      handlePersonalDetailsChange(
                        "dateOfBirth",
                        event.target.value,
                      )
                    }
                  />
                  <p className="prescription-field-hint">
                    {t("contact_modal_hint_dob_example")}
                  </p>

                  <label htmlFor="prescription-phone">{t("contact_modal_label_phone")}</label>
                  <input
                    id="prescription-phone"
                    type="text"
                    value={personalDetails.phone}
                    onChange={(event) =>
                      handlePersonalDetailsChange("phone", event.target.value)
                    }
                  />
                  <p className="prescription-field-hint">
                    {t("contact_modal_hint_phone_prescription")}
                  </p>

                  <label htmlFor="prescription-email">{t("contact_modal_label_email_required")}</label>
                  <input
                    id="prescription-email"
                    type="email"
                    className={stepTwoErrors.email ? "prescription-invalid-field" : ""}
                    value={personalDetails.email}
                    onChange={(event) =>
                      handlePersonalDetailsChange("email", event.target.value)
                    }
                  />
                  <p className="prescription-field-hint">
                    {t("contact_modal_hint_email_prescription")}
                  </p>
                </div>

                <div className="prescription-insurance-block">
                  <p className="prescription-field-label">{t("contact_modal_label_insurance_required")}</p>
                  <label className="prescription-radio-row">
                    <input
                      type="radio"
                      name="insurance-method"
                      value="publicly-insured"
                      checked={
                        personalDetails.insuranceMethod === "publicly-insured"
                      }
                      onChange={(event) =>
                        handlePersonalDetailsChange(
                          "insuranceMethod",
                          event.target.value,
                        )
                      }
                    />
                    <span>{t("contact_modal_insurance_public")}</span>
                  </label>
                  <label className="prescription-radio-row">
                    <input
                      type="radio"
                      name="insurance-method"
                      value="privately-insured"
                      checked={
                        personalDetails.insuranceMethod === "privately-insured"
                      }
                      onChange={(event) =>
                        handlePersonalDetailsChange(
                          "insuranceMethod",
                          event.target.value,
                        )
                      }
                    />
                    <span>{t("contact_modal_insurance_private")}</span>
                  </label>
                </div>

                <div className="prescription-consent-block">
                  <label className="prescription-consent-row">
                    <input
                      type="checkbox"
                      className={
                        stepTwoErrors.consentRequired
                          ? "prescription-invalid-field"
                          : ""
                      }
                      checked={personalDetails.consentRequired}
                      onChange={(event) =>
                        handlePersonalDetailsChange(
                          "consentRequired",
                          event.target.checked,
                        )
                      }
                    />
                    <span>
                      {t("contact_modal_consent_required")}
                    </span>
                  </label>
                  <p>{t("contact_modal_consent_required_info")}</p>

                  <label className="prescription-consent-row">
                    <input
                      type="checkbox"
                      checked={personalDetails.consentMarketing}
                      onChange={(event) =>
                        handlePersonalDetailsChange(
                          "consentMarketing",
                          event.target.checked,
                        )
                      }
                    />
                    <span>
                      {t("contact_modal_consent_marketing")}
                    </span>
                  </label>
                  <p>{t("contact_modal_consent_marketing_info")}</p>
                </div>

                {!!Object.keys(stepTwoErrors).length && (
                  <p className="prescription-step-error">
                    {t("contact_modal_error_fill_required")}
                  </p>
                )}

                <div className="prescription-modal-actions">
                  <button
                    type="button"
                    className="theme-btn"
                    onClick={handleConfirmPersonalDetails}
                  >
                    {t("contact_modal_confirm_personal_details")}
                  </button>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="prescription-step-three">
                <button
                  type="button"
                  className="prescription-step-back"
                  onClick={() => {
                    setStepThreeError("");
                    setCurrentStep(2);
                  }}
                >
                  <i className="ti-angle-left"></i>
                  <span>{t("contact_modal_back")}</span>
                </button>

                <div
                  className="prescription-step-three-icon"
                  aria-hidden="true"
                >
                  <i className="ti-id-badge"></i>
                </div>

                <h2>
                  {t("contact_modal_medical_card_title")}
                </h2>
                <p>
                  {t("contact_modal_medical_card_text_prescription")}
                </p>

                <div className="prescription-step-three-options">
                  <label className="prescription-step-three-option">
                    <input
                      type="radio"
                      name="medical-card-status"
                      value="collect-pharmacy"
                      checked={medicalCardStatus === "collect-pharmacy"}
                      onChange={(event) => {
                        setMedicalCardStatus(event.target.value);
                        setStepThreeError("");
                      }}
                    />
                    <span>{t("contact_modal_medical_card_collect")}</span>
                  </label>

                  <label className="prescription-step-three-option">
                    <input
                      type="radio"
                      name="medical-card-status"
                      value="come-clinic"
                      checked={medicalCardStatus === "come-clinic"}
                      onChange={(event) => {
                        setMedicalCardStatus(event.target.value);
                        setStepThreeError("");
                      }}
                    />
                    <span>
                      {t("contact_modal_medical_card_come_clinic")}
                    </span>
                  </label>
                </div>

                {stepThreeError && (
                  <p className="prescription-step-error">{stepThreeError}</p>
                )}

                <div className="prescription-modal-actions">
                  <button
                    type="button"
                    className="theme-btn"
                    onClick={handleContinueMedicalCard}
                    disabled={!medicalCardStatus}
                  >
                    {t("contact_modal_continue")}
                  </button>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="prescription-step-review">
                <button
                  type="button"
                  className="prescription-step-back"
                  onClick={() => setCurrentStep(3)}
                >
                  <i className="ti-angle-left"></i>
                  <span>{t("contact_modal_back")}</span>
                </button>

                <div className="prescription-review-note">
                  <div className="prescription-review-note-label">
                    {t("contact_modal_note_quarter_start")}
                  </div>
                  <p>
                    {t("contact_modal_note_quarter_text")}
                  </p>
                </div>

                <h2>{t("contact_modal_review_title")}</h2>
                <p>{t("contact_modal_review_text")}</p>

                <section className="prescription-review-section">
                  <h3>{t("contact_modal_review_medications_comments")}</h3>

                  <div className="prescription-review-subtitle">{t("contact_modal_review_medications")}</div>
                  <div className="prescription-review-list">
                    {!!selectedMedications.length ? (
                      selectedMedications.map((medication) => (
                        <div
                          className="prescription-review-medication"
                          key={medication.id}
                        >
                          <div className="prescription-review-medication-icon">
                            <i className="fi flaticon-capsule"></i>
                          </div>
                          <div>
                            <strong>
                              {medication.name}
                              {medication.strength
                                ? ` ${medication.strength}`
                                : ""}
                            </strong>
                            <p>
                              {medication.pzn
                                ? `PZN: ${medication.pzn}`
                                : medication.dosageForm || t("contact_modal_no_additional_details")}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="prescription-review-empty">
                        {t("contact_modal_no_medication_selected")}
                      </div>
                    )}
                  </div>

                  <div className="prescription-review-subtitle">{t("contact_modal_review_comments")}</div>
                  <div className="prescription-review-comment">
                    {prescriptionComment || t("contact_modal_no_comment")}
                  </div>

                  <button
                    type="button"
                    className="prescription-review-edit-btn"
                    onClick={() => setCurrentStep(1)}
                  >
                    {t("contact_modal_edit_medications_comments")}
                  </button>
                </section>

                <section className="prescription-review-section">
                  <h3>{t("contact_modal_review_personal_details")}</h3>

                  <div className="prescription-review-personal-line">
                    {reviewNameLine}
                  </div>
                  <div className="prescription-review-contact-line">
                    <i className="ti-mobile"></i>
                    <span>{personalDetails.phone || t("contact_modal_phone_not_provided")}</span>
                  </div>
                  <div className="prescription-review-contact-line">
                    <i className="ti-email"></i>
                    <span>{personalDetails.email || t("contact_modal_email_not_provided")}</span>
                  </div>
                  <div className="prescription-review-contact-line">
                    <i className="ti-id-badge"></i>
                    <span>{insuranceMethodLabel}</span>
                  </div>
                  <div className="prescription-review-contact-line">
                    <i className="ti-check-box"></i>
                    <span>{medicalCardStatusLabel}</span>
                  </div>

                  <button
                    type="button"
                    className="prescription-review-edit-btn"
                    onClick={() => setCurrentStep(2)}
                  >
                    {t("contact_modal_edit_personal_details")}
                  </button>
                </section>

                <div className="prescription-paper-note">
                  <div className="prescription-paper-note-copy">
                    <p>
                      {t("contact_modal_paper_rx_intro")}
                    </p>
                    <ol>
                      <li>{t("contact_modal_paper_rx_item_1")}</li>
                      <li>
                        {t("contact_modal_paper_rx_item_2")}
                      </li>
                    </ol>
                    <p>
                      {t("contact_modal_paper_rx_outro")}
                    </p>
                  </div>
                  <div className="prescription-paper-note-art" aria-hidden="true">
                    <i className="ti-write"></i>
                  </div>
                </div>

                <div className="prescription-modal-actions">
                  <button
                    type="button"
                    className="theme-btn"
                    onClick={handleSendOrder}
                    disabled={sendOrderLoading}
                  >
                    {sendOrderLoading
                      ? t("contact_modal_sending")
                      : t("contact_modal_send_order")}
                  </button>
                </div>

                {sendOrderError && (
                  <p className="prescription-step-error">{sendOrderError}</p>
                )}
                {sendOrderSuccess && (
                  <p className="prescription-step-success">{sendOrderSuccess}</p>
                )}
              </div>
            )}
          </div>
        </div>
        </Modal>
      )}

      {showPrescription && (
        <Modal
          open={isReferralModalOpen}
          onClose={closeReferralModal}
          center
          focusTrapped={false}
          classNames={{
            modal: "prescription-modal",
            overlay: "prescription-modal-overlay",
          }}
        >
          <div className="prescription-modal-scroll">
            <div className="prescription-modal-content">
              <div
                className="prescription-stepper"
                aria-label={t("contact_modal_aria_referral_steps")}
              >
                {REFERRAL_STEPS.map((stepTitle, index) => {
                  const stepNumber = index + 1;
                  const isCompleted = stepNumber < currentReferralStep;
                  const isActive = stepNumber === currentReferralStep;
                  const itemClassName = `prescription-step-item${isCompleted ? " is-completed" : ""}${isActive ? " is-active" : ""}`;

                  return (
                    <React.Fragment key={stepTitle}>
                      <div className={itemClassName}>
                        <span
                          className="prescription-step-circle"
                          aria-hidden="true"
                        >
                          {isCompleted ? <i className="ti-check"></i> : stepNumber}
                        </span>
                        <span className="prescription-step-title">
                          {stepTitle}
                        </span>
                      </div>
                      {stepNumber !== REFERRAL_STEPS.length && (
                        <span
                          className="prescription-step-separator"
                          aria-hidden="true"
                        >
                          <i className="ti-angle-right"></i>
                        </span>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <p className="prescription-stepper-mobile-label">
                {t("contact_modal_step_label", { current: currentReferralStep, total: REFERRAL_STEPS.length })} &ndash; {REFERRAL_STEPS[currentReferralStep - 1]}
              </p>

              {currentReferralStep === 1 && (
                <>
                  <div className="prescription-modal-header">
                    <div className="prescription-modal-icon">
                      <i className="fi flaticon-diary"></i>
                    </div>
                    <p className="prescription-modal-kicker">{t("contact_modal_kicker_referral")}</p>
                  </div>
                  <h2>{t("contact_modal_referral_title")}</h2>
                  <p>
                    {t("contact_modal_referral_text")}
                  </p>

                  <div className="prescription-comment-field" style={{ marginTop: 24 }}>
                    <label htmlFor="referral-specialist">{t("contact_modal_select_specialist")}</label>
                    <div
                      className="prescription-custom-select"
                      ref={referralDropdownRef}
                    >
                      <button
                        id="referral-specialist"
                        type="button"
                        className="prescription-custom-select-toggle"
                        onClick={() => {
                          if (!specialistsLoading && specialists.length) {
                            setIsReferralDropdownOpen((current) => !current);
                          }
                        }}
                        aria-haspopup="listbox"
                        aria-expanded={isReferralDropdownOpen}
                        disabled={specialistsLoading || !specialists.length}
                      >
                        <span>{selectedReferralSpecialistLabel}</span>
                        <i
                          className={`ti-angle-${isReferralDropdownOpen ? "up" : "down"}`}
                        ></i>
                      </button>

                      {isReferralDropdownOpen && (
                        <div
                          className="prescription-custom-select-menu"
                          role="listbox"
                          aria-label={t("contact_modal_aria_referral_specialist_options")}
                        >
                          {specialists.map((specialist) => (
                            <button
                              type="button"
                              className={`prescription-custom-select-option${selectedReferralSpecialistId === specialist.id ? " is-selected" : ""}`}
                              key={specialist.id}
                              onClick={() => {
                                setSelectedReferralSpecialistId(specialist.id);
                                setReferralStepOneError("");
                                setIsReferralDropdownOpen(false);
                              }}
                            >
                              {resolveSpecialistLabel(specialist)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {specialistsError && (
                      <p className="prescription-step-error">{specialistsError}</p>
                    )}
                  </div>

                  <div className="prescription-comment-field">
                    <label htmlFor="referral-reason">{t("contact_modal_referral_reason_label")}</label>
                    <textarea
                      id="referral-reason"
                      rows="5"
                      value={referralReason}
                      onChange={(event) => {
                        setReferralReason(event.target.value);
                        setReferralStepOneError("");
                      }}
                    />
                  </div>

                  {referralStepOneError && (
                    <p className="prescription-step-error">{referralStepOneError}</p>
                  )}

                  <div className="prescription-modal-actions">
                    <button
                      type="button"
                      className="theme-btn"
                      onClick={handleConfirmReferralRequest}
                      disabled={!selectedReferralSpecialistId || !referralReason.trim()}
                    >
                      {t("contact_modal_confirm_referral")}
                    </button>
                  </div>
                </>
              )}

              {currentReferralStep === 2 && (
                <div className="prescription-step-two">
                  <button
                    type="button"
                    className="prescription-step-back"
                    onClick={() => {
                      setReferralStepTwoErrors({});
                      setCurrentReferralStep(1);
                    }}
                  >
                    <i className="ti-angle-left"></i>
                    <span>{t("contact_modal_back")}</span>
                  </button>

                  <div className="prescription-modal-header prescription-step-header prescription-step-title-row">
                    <div className="prescription-modal-icon">
                      <i className="fi flaticon-user"></i>
                    </div>
                    <h2>{t("contact_modal_title_personal_details")}</h2>
                  </div>

                  <p>{t("contact_modal_fields_required")}</p>

                  <div className="prescription-form-grid">
                    <label htmlFor="referral-salutation">{t("contact_modal_label_salutation_required")}</label>
                    <div
                      className="prescription-custom-select"
                      ref={salutationDropdownRef}
                    >
                      <button
                        id="referral-salutation"
                        type="button"
                        className={`prescription-custom-select-toggle${referralStepTwoErrors.salutation ? " prescription-invalid-field" : ""}`}
                        onClick={() =>
                          setIsSalutationDropdownOpen((current) => !current)
                        }
                        aria-haspopup="listbox"
                        aria-expanded={isSalutationDropdownOpen}
                      >
                        <span>{selectedSalutationLabel}</span>
                        <i
                          className={`ti-angle-${isSalutationDropdownOpen ? "up" : "down"}`}
                        ></i>
                      </button>

                      {isSalutationDropdownOpen && (
                        <div
                          className="prescription-custom-select-menu"
                          role="listbox"
                          aria-label={t("contact_modal_aria_salutation_options")}
                        >
                          {SALUTATION_OPTIONS.map((option) => (
                            <button
                              type="button"
                              className={`prescription-custom-select-option${personalDetails.salutation === option.value ? " is-selected" : ""}`}
                              key={option.value}
                              onClick={() => {
                                handlePersonalDetailsChange(
                                  "salutation",
                                  option.value,
                                );
                                setReferralStepTwoErrors((current) => {
                                  if (!current.salutation) {
                                    return current;
                                  }

                                  const next = { ...current };
                                  delete next.salutation;
                                  return next;
                                });
                                setIsSalutationDropdownOpen(false);
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <label htmlFor="referral-surname">{t("contact_modal_label_surname_required")}</label>
                    <input
                      id="referral-surname"
                      type="text"
                      className={referralStepTwoErrors.surname ? "prescription-invalid-field" : ""}
                      value={personalDetails.surname}
                      onChange={(event) => {
                        handlePersonalDetailsChange("surname", event.target.value);
                        setReferralStepTwoErrors((current) => {
                          if (!current.surname) {
                            return current;
                          }
                          const next = { ...current };
                          delete next.surname;
                          return next;
                        });
                      }}
                    />
                    <p className="prescription-field-hint">
                      {t("contact_modal_hint_insurance_name")}
                    </p>

                    <label htmlFor="referral-name">{t("contact_modal_label_name_required")}</label>
                    <input
                      id="referral-name"
                      type="text"
                      className={referralStepTwoErrors.name ? "prescription-invalid-field" : ""}
                      value={personalDetails.name}
                      onChange={(event) => {
                        handlePersonalDetailsChange("name", event.target.value);
                        setReferralStepTwoErrors((current) => {
                          if (!current.name) {
                            return current;
                          }
                          const next = { ...current };
                          delete next.name;
                          return next;
                        });
                      }}
                    />
                    <p className="prescription-field-hint">
                      {t("contact_modal_hint_insurance_name")}
                    </p>

                    <label htmlFor="referral-dob">{t("contact_modal_label_dob_required")}</label>
                    <input
                      id="referral-dob"
                      type="text"
                      className={referralStepTwoErrors.dateOfBirth ? "prescription-invalid-field" : ""}
                      placeholder={t("contact_modal_dob_placeholder")}
                      value={personalDetails.dateOfBirth}
                      onChange={(event) => {
                        handlePersonalDetailsChange(
                          "dateOfBirth",
                          event.target.value,
                        );
                        setReferralStepTwoErrors((current) => {
                          if (!current.dateOfBirth) {
                            return current;
                          }
                          const next = { ...current };
                          delete next.dateOfBirth;
                          return next;
                        });
                      }}
                    />
                    <p className="prescription-field-hint">
                      {t("contact_modal_hint_dob_example")}
                    </p>

                    <label htmlFor="referral-phone">{t("contact_modal_label_phone")}</label>
                    <input
                      id="referral-phone"
                      type="text"
                      value={personalDetails.phone}
                      onChange={(event) =>
                        handlePersonalDetailsChange("phone", event.target.value)
                      }
                    />
                    <p className="prescription-field-hint">
                      {t("contact_modal_hint_phone_referral")}
                    </p>

                    <label htmlFor="referral-email">{t("contact_modal_label_email_required")}</label>
                    <input
                      id="referral-email"
                      type="email"
                      className={referralStepTwoErrors.email ? "prescription-invalid-field" : ""}
                      value={personalDetails.email}
                      onChange={(event) => {
                        handlePersonalDetailsChange("email", event.target.value);
                        setReferralStepTwoErrors((current) => {
                          if (!current.email) {
                            return current;
                          }
                          const next = { ...current };
                          delete next.email;
                          return next;
                        });
                      }}
                    />
                    <p className="prescription-field-hint">
                      {t("contact_modal_hint_email_referral")}
                    </p>
                  </div>

                  <div className="prescription-insurance-block">
                    <p className="prescription-field-label">{t("contact_modal_label_insurance_required")}</p>
                    <label className="prescription-radio-row">
                      <input
                        type="radio"
                        name="referral-insurance-method"
                        value="publicly-insured"
                        checked={
                          personalDetails.insuranceMethod === "publicly-insured"
                        }
                        onChange={(event) =>
                          handlePersonalDetailsChange(
                            "insuranceMethod",
                            event.target.value,
                          )
                        }
                      />
                      <span>{t("contact_modal_insurance_public")}</span>
                    </label>
                    <label className="prescription-radio-row">
                      <input
                        type="radio"
                        name="referral-insurance-method"
                        value="privately-insured"
                        checked={
                          personalDetails.insuranceMethod === "privately-insured"
                        }
                        onChange={(event) =>
                          handlePersonalDetailsChange(
                            "insuranceMethod",
                            event.target.value,
                          )
                        }
                      />
                      <span>{t("contact_modal_insurance_private")}</span>
                    </label>
                  </div>

                  <div className="prescription-consent-block">
                    <label className="prescription-consent-row">
                      <input
                        type="checkbox"
                        className={
                          referralStepTwoErrors.consentRequired
                            ? "prescription-invalid-field"
                            : ""
                        }
                        checked={personalDetails.consentRequired}
                        onChange={(event) => {
                          handlePersonalDetailsChange(
                            "consentRequired",
                            event.target.checked,
                          );
                          setReferralStepTwoErrors((current) => {
                            if (!current.consentRequired) {
                              return current;
                            }
                            const next = { ...current };
                            delete next.consentRequired;
                            return next;
                          });
                        }}
                      />
                      <span>
                        {t("contact_modal_consent_required")}
                      </span>
                    </label>
                    <p>{t("contact_modal_consent_required_info")}</p>

                    <label className="prescription-consent-row">
                      <input
                        type="checkbox"
                        checked={personalDetails.consentMarketing}
                        onChange={(event) =>
                          handlePersonalDetailsChange(
                            "consentMarketing",
                            event.target.checked,
                          )
                        }
                      />
                      <span>
                        {t("contact_modal_consent_marketing")}
                      </span>
                    </label>
                    <p>{t("contact_modal_consent_marketing_info")}</p>
                  </div>

                  {!!Object.keys(referralStepTwoErrors).length && (
                    <p className="prescription-step-error">
                      {t("contact_modal_error_fill_required")}
                    </p>
                  )}

                  <div className="prescription-modal-actions">
                    <button
                      type="button"
                      className="theme-btn"
                      onClick={handleConfirmReferralPersonalDetails}
                    >
                      {t("contact_modal_confirm_personal_details")}
                    </button>
                  </div>
                </div>
              )}

              {currentReferralStep === 3 && (
                <div className="prescription-step-three">
                  <button
                    type="button"
                    className="prescription-step-back"
                    onClick={() => {
                      setReferralStepThreeError("");
                      setCurrentReferralStep(2);
                    }}
                  >
                    <i className="ti-angle-left"></i>
                    <span>{t("contact_modal_back")}</span>
                  </button>

                  <div
                    className="prescription-step-three-icon"
                    aria-hidden="true"
                  >
                    <i className="ti-id-badge"></i>
                  </div>

                  <h2>
                    {t("contact_modal_medical_card_title")}
                  </h2>
                  <p>
                    {t("contact_modal_medical_card_text_referral")}
                  </p>

                  <div className="prescription-step-three-options">
                    <label className="prescription-step-three-option">
                      <input
                        type="radio"
                        name="referral-medical-card-status"
                        value="collect-pharmacy"
                        checked={medicalCardStatus === "collect-pharmacy"}
                        onChange={(event) => {
                          setMedicalCardStatus(event.target.value);
                          setReferralStepThreeError("");
                        }}
                      />
                      <span>{t("contact_modal_referral_medical_card_scanned")}</span>
                    </label>

                    <label className="prescription-step-three-option">
                      <input
                        type="radio"
                        name="referral-medical-card-status"
                        value="come-clinic"
                        checked={medicalCardStatus === "come-clinic"}
                        onChange={(event) => {
                          setMedicalCardStatus(event.target.value);
                          setReferralStepThreeError("");
                        }}
                      />
                      <span>
                        {t("contact_modal_medical_card_come_clinic")}
                      </span>
                    </label>
                  </div>

                  {referralStepThreeError && (
                    <p className="prescription-step-error">{referralStepThreeError}</p>
                  )}

                  <div className="prescription-modal-actions">
                    <button
                      type="button"
                      className="theme-btn"
                      onClick={handleContinueReferralMedicalCard}
                      disabled={!medicalCardStatus}
                    >
                      {t("contact_modal_continue")}
                    </button>
                  </div>
                </div>
              )}

              {currentReferralStep === 4 && (
                <div className="prescription-step-review">
                  <button
                    type="button"
                    className="prescription-step-back"
                    onClick={() => setCurrentReferralStep(3)}
                  >
                    <i className="ti-angle-left"></i>
                    <span>{t("contact_modal_back")}</span>
                  </button>

                  <div className="prescription-review-note">
                    <div className="prescription-review-note-label">
                      {t("contact_modal_note_quarter_start")}
                    </div>
                    <p>
                      {t("contact_modal_note_quarter_text")}
                    </p>
                  </div>

                  <h2>{t("contact_modal_review_title")}</h2>
                  <p>{t("contact_modal_review_text")}</p>

                  <section className="prescription-review-section">
                    <h3>{t("contact_modal_review_referral_details")}</h3>

                    <div className="prescription-review-subtitle">{t("contact_modal_review_specialist")}</div>
                    <div className="prescription-review-comment">
                      {selectedReferralSpecialistLabel || t("contact_modal_no_specialist_selected")}
                    </div>

                    <div className="prescription-review-subtitle">{t("contact_modal_review_reason")}</div>
                    <div className="prescription-review-comment">
                      {referralReason || t("contact_modal_no_reason")}
                    </div>

                    <button
                      type="button"
                      className="prescription-review-edit-btn"
                      onClick={() => setCurrentReferralStep(1)}
                    >
                      {t("contact_modal_edit_referral_details")}
                    </button>
                  </section>

                  <section className="prescription-review-section">
                    <h3>{t("contact_modal_review_personal_details")}</h3>

                    <div className="prescription-review-personal-line">
                      {reviewNameLine}
                    </div>
                    <div className="prescription-review-contact-line">
                      <i className="ti-mobile"></i>
                      <span>{personalDetails.phone || t("contact_modal_phone_not_provided")}</span>
                    </div>
                    <div className="prescription-review-contact-line">
                      <i className="ti-email"></i>
                      <span>{personalDetails.email || t("contact_modal_email_not_provided")}</span>
                    </div>
                    <div className="prescription-review-contact-line">
                      <i className="ti-id-badge"></i>
                      <span>{insuranceMethodLabel}</span>
                    </div>
                    <div className="prescription-review-contact-line">
                      <i className="ti-check-box"></i>
                      <span>{referralMedicalCardStatusLabel}</span>
                    </div>

                    <button
                      type="button"
                      className="prescription-review-edit-btn"
                      onClick={() => setCurrentReferralStep(2)}
                    >
                      {t("contact_modal_edit_personal_details")}
                    </button>
                  </section>

                  <div className="prescription-modal-actions">
                    <button
                      type="button"
                      className="theme-btn"
                      onClick={handleSendReferral}
                      disabled={sendReferralLoading}
                    >
                      {sendReferralLoading
                        ? t("contact_modal_sending")
                        : t("contact_modal_send_referral")}
                    </button>
                  </div>

                  {sendReferralError && (
                    <p className="prescription-step-error">{sendReferralError}</p>
                  )}
                  {sendReferralSuccess && (
                    <p className="prescription-step-success">{sendReferralSuccess}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default Contactpage;
